import { formatTime, formatToYMD } from '@/utils/date.js';
import { formatNumber } from '@/utils/number.js';

const EMPTY_CONDITION_VALUE = '–';

/**
 * A rule's condition tree (and the logged snapshot of it) is a recursive
 * { logic, items } structure. A node is a group when it carries both keys;
 * otherwise it is a leaf condition row.
 */
export function isConditionGroup(node) {
  return !!node
    && typeof node === 'object'
    && 'logic' in node
    && 'items' in node;
}

/**
 * Resolve a leaf's `field` machine name to its descriptor from the
 * `app/fields` store list (holds the human label, data_type, date_periods…).
 */
export function findConditionField(machineName, fields) {
  if (!Array.isArray(fields)) {
    return null;
  }

  return fields.find(field => field.machine_name === machineName) ?? null;
}

/**
 * Resolve a leaf's `comparison` machine name to its descriptor from the
 * `app/fieldComparisons` store list.
 */
export function findConditionComparison(machineName, comparisons) {
  if (!Array.isArray(comparisons)) {
    return null;
  }

  return comparisons.find(comparison => comparison.machine_name === machineName) ?? null;
}

/**
 * Format a condition value (either the configured threshold or the measured
 * `current_value` from the snapshot) according to the field's data type.
 *
 * Unit gotchas handled here:
 *  - money is stored in the account's major currency unit (NOT cents) → only
 *    add the symbol and use the currency's decimal count.
 *  - CTR fields are already a percentage number (1.5 → 1.5%) → append '%', never ×100.
 *  - dynamic comparisons ("is within the top 10%") store a 0..100 percentile on
 *    both the threshold and the measured side, regardless of the field's own unit.
 */
export function formatConditionValue(value, field, comparison, context = {}) {
  const { currency = null, languages = [] } = context;

  if (value === null || value === undefined || value === '') {
    return EMPTY_CONDITION_VALUE;
  }

  if (comparison && comparison.dynamic_num) {
    return formatNumber(value, '%', 0, true);
  }

  const dataType = field ? field.data_type : null;
  const machineName = field ? field.machine_name : '';

  switch (dataType) {
    case 'money': {
      const decimals = currency && currency.offset === 1 ? 0 : 2;
      const amount = formatNumber(value, null, decimals, true);

      if (currency && currency.symbol) {
        return currency.symbol.length === 1
          ? currency.symbol + amount
          : amount + ' ' + currency.symbol;
      }

      return amount;
    }

    case 'percentage':
      return formatNumber(value, '%', 2, true);

    case 'int':
      return formatNumber(value, null, 0, true);

    case 'float':
      if (machineName.includes('ctr')) {
        return formatNumber(value, '%', 2, true);
      }
      if (machineName.includes('roas')) {
        return formatNumber(value, '×', 2, true);
      }
      return formatNumber(value, null, 2, true);

    case 'datetime': {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }
      return formatToYMD(date) + ' ' + formatTime(date);
    }

    case 'language': {
      const language = Array.isArray(languages)
        ? languages.find(item => item.iso_639_1 === value)
        : null;
      return language ? language.name : String(value);
    }

    case 'string':
    default:
      return String(value);
  }
}
